import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Box, Card, CardContent, Button, Stack, Typography } from "@mui/material";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { FieldSelect, FieldTextField } from "../../../components/selections/fieldInputs";
import { stepCardSx, stepActionsSx, backBtnSx, nextBtnSx, StepHeader } from "../../campaign/components/stepUi";
import languages from "../../../components/languages/languages";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import { setTemplateNameData, setActiveNext, setActiveBack } from "../templatesSlice";

export default function TemplatesCreation() {
    const { activeStep, templateNameData } = useSelector((state) => state.templates);
    const dispatch = useDispatch();

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [state, setState] = useState({
        selectedCategory: "",
        templateName: "",
        selectedLanguage: "",
        errors: {}
    });

    const { selectedCategory, templateName, selectedLanguage } = state;

    const templateFields = [
        { id: 1, label: "Category", type: "select", name: "selectedCategory", value: selectedCategory, options: [{ value: 1, label: "Marketing" }, { value: 2, label: "Utility" }], textLabel: "Choose a category that best describes your message template" },
        { id: 2, label: "Language", type: "select", name: "selectedLanguage", value: selectedLanguage, options: languages, textLabel: "Choose languages for your message template. You can delete or add more languages later" },
        { id: 3, label: "Template Name", type: "text", name: "templateName", value: templateName, textLabel: "Name Your Message Template" },
    ];

    const handleSingleSelectChange = (name, selectedValue) => {
        setState((prev) => ({
            ...prev,
            [name]: selectedValue,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    const handleChange = (name, value) => {
        let newValue = value;

        if (name === "templateName") {
            newValue = value.replace(/^\s+/, "").replace(/[^a-z0-9_]/ig, '').toLowerCase().slice(0, 150);
        }

        setState((prev) => ({
            ...prev,
            [name]: newValue,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    const validateStep = () => {
        const validations = [
            { valid: !!selectedCategory, message: "Category is required", field: "selectedCategory" },
            { valid: !!selectedLanguage, message: "Language is required", field: "selectedLanguage" },
            { valid: !!templateName, message: "Template Name is required", field: "templateName" },
        ];

        const newErrors = {};
        let isValid = true;

        for (let rule of validations) {
            if (!rule.valid) {
                setSnackbar((prev) => ({
                    ...prev,
                    open: true,
                    message: rule.message,
                    severity: "error",
                }));

                setState((prev) => ({
                    ...prev,
                    errors: { ...prev.errors, [rule.field]: rule.message },
                }));

                isValid = false;
                return;
            }
        }

        setState((prev) => ({ ...prev, errors: { ...prev.errors, ...newErrors } }));

        return isValid;
    };

    const handleNext = () => {
        if (activeStep === 0) {
            if (!validateStep()) return;
            dispatch(
                setTemplateNameData([
                    {
                        templateName: templateName,
                        language: selectedLanguage,
                        categoryId: selectedCategory,
                        categoryName: [
                            { value: 1, label: "Marketing" },
                            { value: 2, label: "Utility" },
                        ].find((item) => item.value === selectedCategory)?.label,
                    },
                ])
            );
        }

        dispatch(setActiveNext());
    };

    const handleBack = () => {
        dispatch(setActiveBack());
    };

    useEffect(() => {
        if (templateNameData?.length > 0) {
            const { templateName, language, categoryId } = templateNameData[0];
            setState((prev) => ({
                ...prev,
                templateName: templateName,
                selectedLanguage: language,
                selectedCategory: categoryId,
            }));
        }
    }, [templateNameData]);

    // Icons / placeholders per field (display only)
    const fieldUi = {
        selectedCategory: { icon: <CategoryOutlinedIcon />, placeholder: "Choose a category" },
        selectedLanguage: { icon: <TranslateIcon />, placeholder: "Choose a language" },
        templateName: { icon: <DescriptionOutlinedIcon />, placeholder: "e.g. order_update" },
    };

    return (
        <div>
            <Card sx={stepCardSx}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <StepHeader
                        icon={<DescriptionOutlinedIcon sx={{ fontSize: 20 }} />}
                        title="Template Details"
                        subtitle="Pick a category and language, then name your template"
                    />

                    <Grid container spacing={2.5}>
                        {templateFields.map((field) => (
                            <Grid key={field.id} size={{ xs: 12, md: 4 }}>
                                {field.type === "select" ? (
                                    <FieldSelect
                                        name={field.name}
                                        label={field.label}
                                        options={field.options}
                                        value={field.value}
                                        onChange={handleSingleSelectChange}
                                        error={!!state.errors[field.name]}
                                        required
                                        icon={fieldUi[field.name].icon}
                                        placeholder={fieldUi[field.name].placeholder}
                                    />
                                ) : field.type === "text" ? (
                                    <FieldTextField
                                        name={field.name}
                                        label={field.label}
                                        value={field.value}
                                        onChange={(e) => handleChange(field.name, e.target.value)}
                                        error={!!state.errors[field.name]}
                                        maxLength={60}
                                        required
                                        icon={fieldUi[field.name].icon}
                                        placeholder={fieldUi[field.name].placeholder}
                                        onKeyDown={(e) => {
                                            if (e.key === " " || e.key === "Spacebar") {
                                                e.preventDefault();
                                            }
                                        }}
                                    />
                                ) : null}
                            </Grid>
                        ))}
                    </Grid>

                    {/* Name rule hint */}
                    <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "#94a3b8" }}>
                        Template name: lowercase letters, numbers and underscores only.
                    </Typography>
                </CardContent>

                <Box sx={stepActionsSx}>
                    <Stack direction="row" spacing={1.5}>
                        <Box sx={{ cursor: activeStep === 0 ? "not-allowed" : "pointer" }}>
                            <Button disabled={activeStep === 0} onClick={handleBack} sx={backBtnSx}>
                                Back
                            </Button>
                        </Box>
                        <Button onClick={handleNext} disabled={activeStep >= 2} sx={nextBtnSx}>
                            Next
                        </Button>
                    </Stack>
                </Box>
            </Card>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </div>
    );
}
